import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { Plus, Cpu, Box as BoxIcon, Pencil, Trash2, Lock, Search, DollarSign } from 'lucide-react';
import modelsService from '../../services/modelsService';
import platformAdminService from '../../services/platformAdminService';
import { repriceUsage } from '../../services/usageAnalyticsService';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import PaginationBar from './PaginationBar';
import styles from './PlatformAdmin.module.scss';

const PAGE_SIZE = 50;

const emptyProvider = { id: null, name: '', display_name: '', description: '', is_active: true };
const emptyModel = {
  id: null,
  provider_id: '',
  model_id: '',
  name: '',
  display_name: '',
  description: '',
  context_window: '',
  is_active: true,
  input_price_per_million: '',
  output_price_per_million: '',
  price_currency: 'USD',
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

// A model with no configured price is "Unpriced", not free — spec §6.
const formatPricing = (m) => {
  const inP = Number(m.input_price_per_million);
  const outP = Number(m.output_price_per_million);
  if (!inP && !outP) return 'Unpriced';
  const currency = m.price_currency || 'USD';
  return `In $${inP.toFixed(2)} / Out $${outP.toFixed(2)} per 1M ${currency}`;
};

/**
 * LLM Catalog panel (spec §2.2 / §3.2 `platform_llm_catalog`).
 *
 * Provider + model CRUD, moved off the normal (read-only) Models screen into the
 * Platform Admin console. Mutations are platform-only on the backend; this panel
 * reaches them with the admin's own x-api-key via modelsService.
 */
const LLMCatalogPanel = ({ notify }) => {
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modelSearch, setModelSearch] = useState('');
  const debouncedModelSearch = useDebouncedValue(modelSearch, 400);
  const [modelOffset, setModelOffset] = useState(0);
  const [modelTotal, setModelTotal] = useState(0);

  const [providerDialog, setProviderDialog] = useState(null); // { mode }
  const [providerForm, setProviderForm] = useState(emptyProvider);
  const [modelDialog, setModelDialog] = useState(null); // { mode }
  const [modelForm, setModelForm] = useState(emptyModel);
  const [saving, setSaving] = useState(false);

  const [confirm, setConfirm] = useState(null); // { type: 'provider'|'model', id, name }
  const [busy, setBusy] = useState(false);

  const [repriceDialog, setRepriceDialog] = useState(null); // { model }
  const [repriceForm, setRepriceForm] = useState({ start: '', end: '', force: false });
  const [repriceBusy, setRepriceBusy] = useState(false);

  // Providers load fully (they populate the model dialog's dropdown).
  const loadProviders = useCallback(async () => {
    try {
      setProvidersLoading(true);
      const data = await platformAdminService.getProviders(false);
      setProviders(Array.isArray(data) ? data : data?.providers || []);
    } catch (err) {
      notify(err?.message || 'Failed to load providers.', 'error');
    } finally {
      setProvidersLoading(false);
    }
  }, [notify]);

  // Models are searchable + paginated (server-side).
  const loadModels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await platformAdminService.listModels({
        activeOnly: false,
        search: debouncedModelSearch || undefined,
        limit: PAGE_SIZE,
        offset: modelOffset,
      });
      const list = Array.isArray(data) ? data : data?.models || [];
      setModels(list);
      setModelTotal(Number.isFinite(data?.total) ? data.total : list.length);
    } catch (err) {
      setError(err?.message || 'Failed to load models.');
    } finally {
      setLoading(false);
    }
  }, [debouncedModelSearch, modelOffset]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const providerLabel = useCallback(
    (id) => providers.find((p) => p.id === id)?.display_name || providers.find((p) => p.id === id)?.name || 'Unknown',
    [providers]
  );

  const getModelContext = (m) =>
    m.context_window ?? m.max_context_tokens ?? m.context ?? m.metadata?.context ?? null;

  // ── Providers ──
  const openProviderCreate = () => {
    setProviderForm({ ...emptyProvider });
    setProviderDialog({ mode: 'create' });
  };
  const openProviderEdit = (p) => {
    setProviderForm({
      id: p.id,
      name: p.name || '',
      display_name: p.display_name || '',
      description: p.description || '',
      is_active: p.is_active !== false,
    });
    setProviderDialog({ mode: 'edit' });
  };

  const saveProvider = async () => {
    const payload = {
      name: providerForm.name.trim(),
      display_name: providerForm.display_name.trim(),
      description: providerForm.description.trim(),
      is_active: providerForm.is_active,
    };
    const { isValid, errors } = modelsService.validateProviderData(payload);
    if (!isValid) {
      notify(errors.join(' · '), 'error');
      return;
    }
    try {
      setSaving(true);
      if (providerDialog?.mode === 'edit') {
        await modelsService.updateProvider(providerForm.id, payload);
        notify('Provider updated.', 'success');
      } else {
        await modelsService.createProvider(payload);
        notify('Provider created.', 'success');
      }
      setProviderDialog(null);
      await loadProviders();
      await loadModels();
    } catch (err) {
      notify(err?.data?.detail || err?.message || 'Failed to save provider.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Models ──
  const openModelCreate = () => {
    setModelForm({ ...emptyModel });
    setModelDialog({ mode: 'create' });
  };
  const openModelEdit = (m) => {
    setModelForm({
      id: m.id,
      provider_id: m.provider_id || '',
      model_id: m.model_id || m.name || '',
      name: m.name || '',
      display_name: m.display_name || '',
      description: m.description || '',
      context_window: getModelContext(m) ?? '',
      is_active: m.is_active !== false,
      input_price_per_million: m.input_price_per_million ?? '',
      output_price_per_million: m.output_price_per_million ?? '',
      price_currency: m.price_currency || 'USD',
    });
    setModelDialog({ mode: 'edit' });
  };

  const saveModel = async () => {
    // `model_id` is the provider-side identifier the backend calls with
    // (e.g. "google/gemini-3-flash-preview"); it defaults to the model name,
    // which is what admins have historically typed the full slug into.
    const payload = {
      provider_id: modelForm.provider_id,
      model_id: (modelForm.model_id || modelForm.name).trim(),
      name: modelForm.name.trim(),
      display_name: modelForm.display_name.trim(),
      description: modelForm.description.trim(),
      is_active: modelForm.is_active,
    };
    if (modelForm.context_window !== '' && modelForm.context_window != null) {
      const n = Number(modelForm.context_window);
      if (!Number.isNaN(n)) payload.context_window = n;
    }
    if (modelForm.input_price_per_million !== '' && modelForm.input_price_per_million != null) {
      const n = Number(modelForm.input_price_per_million);
      if (!Number.isNaN(n)) payload.input_price_per_million = n;
    }
    if (modelForm.output_price_per_million !== '' && modelForm.output_price_per_million != null) {
      const n = Number(modelForm.output_price_per_million);
      if (!Number.isNaN(n)) payload.output_price_per_million = n;
    }
    if (payload.input_price_per_million != null || payload.output_price_per_million != null) {
      payload.price_currency = (modelForm.price_currency || 'USD').trim().toUpperCase();
    }
    const { isValid, errors } = modelsService.validateModelData(payload);
    if (!isValid) {
      notify(errors.join(' · '), 'error');
      return;
    }
    try {
      setSaving(true);
      if (modelDialog?.mode === 'edit') {
        await modelsService.updateModel(modelForm.id, payload);
        notify('Model updated.', 'success');
      } else {
        await modelsService.createModel(payload);
        notify('Model created.', 'success');
      }
      setModelDialog(null);
      await loadModels();
    } catch (err) {
      notify(err?.data?.detail || err?.message || 'Failed to save model.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const runDelete = async () => {
    if (!confirm) return;
    try {
      setBusy(true);
      if (confirm.type === 'provider') {
        await modelsService.deleteProvider(confirm.id);
        notify('Provider deleted.', 'info');
      } else {
        await modelsService.deleteModel(confirm.id);
        notify('Model deleted.', 'info');
      }
      setConfirm(null);
      if (confirm.type === 'provider') await loadProviders();
      await loadModels();
    } catch (err) {
      notify(err?.data?.detail || err?.message || 'Delete failed.', 'error');
    } finally {
      setBusy(false);
    }
  };

  // Reprice history (spec §8) — an explicit maintenance action, never an implicit
  // side effect of saving a price. Only touches rows that are unpriced or were
  // previously re-priced unless `force` is set.
  const runReprice = async () => {
    if (!repriceDialog?.model) return;
    try {
      setRepriceBusy(true);
      const result = await repriceUsage({
        llm_model_id: repriceDialog.model.id,
        start: repriceForm.start,
        end: repriceForm.end,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        force: repriceForm.force,
      });
      notify(
        `Re-priced ${result?.updated ?? 0} of ${result?.matched ?? 0} matching run${result?.matched === 1 ? '' : 's'}.`,
        'success'
      );
      setRepriceDialog(null);
    } catch (err) {
      notify(err?.data?.detail || err?.message || 'Reprice failed.', 'error');
    } finally {
      setRepriceBusy(false);
    }
  };

  const sortedModels = useMemo(
    () => [...models].sort((a, b) => (a.provider_id || 0) - (b.provider_id || 0)),
    [models]
  );

  return (
    <>
      <Box className={styles.noteBanner}>
        <Cpu size={18} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Manage the global LLM provider and model catalog. These are platform-wide; the read-only
          Models screen shows the same list to everyone. Company/user API keys are configured elsewhere.
        </span>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Providers */}
      <Box className={styles.toolbar}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          LLM Providers ({providers.length})
        </Typography>
        <Button variant="contained" startIcon={<Plus size={18} />} className={styles.primaryBtn} onClick={openProviderCreate}>
          New provider
        </Button>
      </Box>
      {providersLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Box className={styles.tableCard}>
            {providers.length === 0 ? (
              <Box className={styles.usersEmpty}>No providers configured.</Box>
            ) : (
              providers.map((p) => (
                <Box key={p.id} className={styles.userRow} sx={{ flexWrap: 'wrap' }}>
                  <Box className={styles.companyMain} sx={{ flex: '1 1 260px' }}>
                    <span className={styles.companyAvatar}><Cpu size={18} /></span>
                    <Box>
                      <span className={styles.companyName}>
                        {p.display_name || p.name}
                        <span className={styles.companyMeta}> ({p.name})</span>
                      </span>
                      <span className={styles.companyMeta}>{p.description || '—'}</span>
                    </Box>
                  </Box>
                  <span className={`${styles.statusPill} ${p.is_active ? styles.active : styles.inactive}`}>
                    {p.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <Box className={styles.actionsCell}>
                    <Tooltip title="Edit">
                      <IconButton size="small" className={styles.subtleBtn} onClick={() => openProviderEdit(p)}>
                        <Pencil size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        className={styles.dangerIcon}
                        onClick={() => setConfirm({ type: 'provider', id: p.id, name: p.display_name || p.name })}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))
            )}
        </Box>
      )}

      {/* Models */}
      <Box className={styles.toolbar} sx={{ mt: 1 }}>
        <Box className={styles.filterGroup}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            LLM Models ({modelTotal})
          </Typography>
          <TextField
            size="small"
            placeholder="Search models…"
            value={modelSearch}
            onChange={(e) => { setModelSearch(e.target.value); setModelOffset(0); }}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          className={styles.primaryBtn}
          onClick={openModelCreate}
          disabled={providers.length === 0}
        >
          New model
        </Button>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Box className={styles.tableCard}>
            {sortedModels.length === 0 ? (
              <Box className={styles.usersEmpty}>No models configured.</Box>
            ) : (
              sortedModels.map((m) => {
                const ctx = getModelContext(m);
                return (
                  <Box key={m.id} className={styles.userRow} sx={{ flexWrap: 'wrap' }}>
                    <Box className={styles.companyMain} sx={{ flex: '1 1 260px' }}>
                      <span className={styles.companyAvatar}><BoxIcon size={18} /></span>
                      <Box>
                        <span className={styles.companyName}>{m.display_name || m.name}</span>
                        <span className={styles.companyMeta}>
                          {providerLabel(m.provider_id)}{ctx != null && ctx !== '' ? ` · ctx ${ctx}` : ''} · {formatPricing(m)}
                        </span>
                      </Box>
                    </Box>
                    <span className={`${styles.statusPill} ${m.is_active ? styles.active : styles.inactive}`}>
                      {m.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <Box className={styles.actionsCell}>
                      <Tooltip title="Apply current price to historical usage">
                        <IconButton
                          size="small"
                          className={styles.subtleBtn}
                          onClick={() => {
                            setRepriceForm({ start: daysAgoStr(30), end: todayStr(), force: false });
                            setRepriceDialog({ model: m });
                          }}
                        >
                          <DollarSign size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" className={styles.subtleBtn} onClick={() => openModelEdit(m)}>
                          <Pencil size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          className={styles.dangerIcon}
                          onClick={() => setConfirm({ type: 'model', id: m.id, name: m.display_name || m.name })}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })
            )}
        </Box>
      )}

      {modelTotal > 0 && (
        <PaginationBar
          total={modelTotal}
          limit={PAGE_SIZE}
          offset={modelOffset}
          loading={loading}
          unit="models"
          onOffsetChange={setModelOffset}
        />
      )}

      {/* Provider dialog */}
      <Dialog open={!!providerDialog} onClose={() => !saving && setProviderDialog(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>
          {providerDialog?.mode === 'edit' ? 'Edit provider' : 'New provider'}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <TextField
            autoFocus label="Name (slug, e.g. openai)" fullWidth variant="outlined"
            value={providerForm.name} onChange={(e) => setProviderForm({ ...providerForm, name: e.target.value })}
            className={styles.formField}
          />
          <TextField
            label="Display name" fullWidth variant="outlined"
            value={providerForm.display_name} onChange={(e) => setProviderForm({ ...providerForm, display_name: e.target.value })}
            className={styles.formField}
          />
          <TextField
            label="Description" fullWidth multiline minRows={2} variant="outlined"
            value={providerForm.description} onChange={(e) => setProviderForm({ ...providerForm, description: e.target.value })}
            className={styles.formField}
          />
          <FormControlLabel
            control={<Switch checked={providerForm.is_active} onChange={(e) => setProviderForm({ ...providerForm, is_active: e.target.checked })} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setProviderDialog(null)} disabled={saving} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={saveProvider} disabled={saving} className={styles.primaryBtn}>
            {saving ? <CircularProgress size={20} color="inherit" /> : providerDialog?.mode === 'edit' ? 'Save changes' : 'Create provider'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Model dialog */}
      <Dialog open={!!modelDialog} onClose={() => !saving && setModelDialog(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>
          {modelDialog?.mode === 'edit' ? 'Edit model' : 'New model'}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <FormControl fullWidth>
            <InputLabel>Provider</InputLabel>
            <Select
              label="Provider"
              value={modelForm.provider_id}
              onChange={(e) => setModelForm({ ...modelForm, provider_id: e.target.value })}
            >
              {providers.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.display_name || p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Model name (e.g. gpt-4o)" fullWidth variant="outlined"
            value={modelForm.name} onChange={(e) => setModelForm({ ...modelForm, name: e.target.value })}
            className={styles.formField}
          />
          <TextField
            label="Model ID (provider identifier)" fullWidth variant="outlined"
            value={modelForm.model_id} onChange={(e) => setModelForm({ ...modelForm, model_id: e.target.value })}
            className={styles.formField}
            placeholder={modelForm.name || 'google/gemini-3-flash-preview'}
            helperText="Exact identifier sent to the provider. Leave blank to reuse the model name."
          />
          <TextField
            label="Display name" fullWidth variant="outlined"
            value={modelForm.display_name} onChange={(e) => setModelForm({ ...modelForm, display_name: e.target.value })}
            className={styles.formField}
          />
          <TextField
            label="Description" fullWidth multiline minRows={2} variant="outlined"
            value={modelForm.description} onChange={(e) => setModelForm({ ...modelForm, description: e.target.value })}
            className={styles.formField}
          />
          <TextField
            label="Context window (tokens)" type="number" fullWidth variant="outlined"
            value={modelForm.context_window} onChange={(e) => setModelForm({ ...modelForm, context_window: e.target.value })}
            className={styles.formField}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Input price (per 1M tokens)" type="number" fullWidth variant="outlined"
              value={modelForm.input_price_per_million}
              onChange={(e) => setModelForm({ ...modelForm, input_price_per_million: e.target.value })}
              className={styles.formField}
            />
            <TextField
              label="Output price (per 1M tokens)" type="number" fullWidth variant="outlined"
              value={modelForm.output_price_per_million}
              onChange={(e) => setModelForm({ ...modelForm, output_price_per_million: e.target.value })}
              className={styles.formField}
            />
            <TextField
              label="Currency" fullWidth variant="outlined"
              value={modelForm.price_currency}
              onChange={(e) => setModelForm({ ...modelForm, price_currency: e.target.value })}
              className={styles.formField}
              helperText="Only USD is aggregated in usage cost totals today."
            />
          </Box>
          <FormControlLabel
            control={<Switch checked={modelForm.is_active} onChange={(e) => setModelForm({ ...modelForm, is_active: e.target.checked })} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setModelDialog(null)} disabled={saving} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={saveModel} disabled={saving} className={styles.primaryBtn}>
            {saving ? <CircularProgress size={20} color="inherit" /> : modelDialog?.mode === 'edit' ? 'Save changes' : 'Create model'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Delete {confirm?.type}</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><Lock /></Box>
          <Typography className={styles.deleteHeading}>Delete "{confirm?.name}"?</Typography>
          <Typography className={styles.deleteText}>
            {confirm?.type === 'provider'
              ? 'Removing a provider may affect its models and any agents relying on it.'
              : 'Agents referencing this model may stop working until reconfigured.'}
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setConfirm(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={runDelete} disabled={busy} className={styles.dangerButton}>
            {busy ? 'Working...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reprice history (spec §8) */}
      <Dialog open={!!repriceDialog} onClose={() => !repriceBusy && setRepriceDialog(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>
          Apply current price to historical usage
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
            Stamps {repriceDialog?.model?.display_name || repriceDialog?.model?.name}'s current price
            ({formatPricing(repriceDialog?.model || {})}) onto runs recorded before it had one. By default
            this only touches unpriced rows — it never silently restates a period that was already reported.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start" type="date" fullWidth variant="outlined" InputLabelProps={{ shrink: true }}
              value={repriceForm.start} onChange={(e) => setRepriceForm({ ...repriceForm, start: e.target.value })}
              className={styles.formField}
            />
            <TextField
              label="End" type="date" fullWidth variant="outlined" InputLabelProps={{ shrink: true }}
              value={repriceForm.end} onChange={(e) => setRepriceForm({ ...repriceForm, end: e.target.value })}
              className={styles.formField}
            />
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={repriceForm.force}
                onChange={(e) => setRepriceForm({ ...repriceForm, force: e.target.checked })}
              />
            }
            label="Force — also re-stamp rows that already have a price"
          />
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setRepriceDialog(null)} disabled={repriceBusy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={runReprice} disabled={repriceBusy} className={styles.primaryBtn}>
            {repriceBusy ? <CircularProgress size={20} color="inherit" /> : 'Apply price'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LLMCatalogPanel;
